/**
 * PopOutList — generic hover-to-expand cell for table rows with multiple items.
 *
 * Shows a fixed-height collapsed preview with a gradient fade, then portals an
 * expanded card on hover. Used by PopOutEvents (SalesOrderTable) and
 * ConfigurationCell (Inventory).
 */

import { type ReactNode, useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

export const POPOUT_PADDING = 12;
const POPOUT_BORDER = 1;
export const DEFAULT_COLLAPSED_HEIGHT = 42;
const CLOSE_DELAY = 80;

/**
 * Horizontal space the card spends on its own padding and border. A caller
 * that needs the expanded content to be exactly as wide as its collapsed
 * preview — so prose re-wraps identically and no word appears to move when the
 * popout opens — passes `collapsedWidth + POPOUT_CHROME_WIDTH` as `width`.
 */
export const POPOUT_CHROME_WIDTH = 2 * (POPOUT_PADDING + POPOUT_BORDER);

interface PopOutListProps {
  /** Content shown in the collapsed (fixed-height) cell */
  collapsed: ReactNode;
  /** Header content rendered above the divider in the expanded popout */
  header?: ReactNode;
  /** Content shown below the divider in the expanded portal card */
  expanded: ReactNode;
  /** Width of the expanded popout (defaults to 400) */
  width?: number;
  /** Fixed height for the collapsed preview (defaults to 42) */
  collapsedHeight?: number;
  /** CSS height of the preview's bottom fade (defaults to the full preview) */
  previewFadeHeight?: string;
  /** Extra top offset for the popout position (e.g. to account for a header above content) */
  headerOffset?: number;
  /** Start in the open state (useful for Storybook) */
  defaultOpen?: boolean;
  /** Called when the popout closes */
  onClose?: () => void;
}

export function PopOutList({
  collapsed,
  header,
  expanded,
  width = 400,
  collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
  previewFadeHeight = '100%',
  headerOffset = 0,
  defaultOpen = false,
  onClose,
}: PopOutListProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [fixedPos, setFixedPos] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const popoutRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const targetPosRef = useRef<{ top: number; left: number }>({ top: 0, left: 0 });
  // Tracks open synchronously so handleOpen can't re-fire and re-anchor the
  // popout while it's already open (see handleOpen).
  const openRef = useRef(defaultOpen);
  // True between pointerdown and pointerup inside the popout — a drag-select or
  // scrollbar-thumb drag that strays past the card's edge must not close it out
  // from under the drag.
  const selectingRef = useRef(false);

  const close = useCallback(() => {
    openRef.current = false;
    setOpen(false);
    onClose?.();
  }, [onClose]);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // True while the user is reading the popout with intent to copy: either
  // mid-drag, or holding a finished selection inside it. Closing in either
  // case destroys the selection along with the node, which makes the text
  // impossible to copy without opening the record itself.
  const hasLiveSelection = useCallback(() => {
    const popout = popoutRef.current;
    if (!popout) return false;
    if (selectingRef.current) return true;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false;
    return popout.contains(selection.getRangeAt(0).commonAncestorContainer);
  }, []);

  const scheduleClose = useCallback(() => {
    if (defaultOpen) return;
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      if (hasLiveSelection()) return;
      close();
    }, CLOSE_DELAY);
  }, [cancelClose, close, defaultOpen, hasLiveSelection]);

  useEffect(() => cancelClose, [cancelClose]);

  // Set initial position when defaultOpen
  useEffect(() => {
    if (defaultOpen && containerRef.current && !fixedPos) {
      const rect = containerRef.current.getBoundingClientRect();
      setFixedPos({
        top: rect.top - POPOUT_PADDING - POPOUT_BORDER - headerOffset,
        left: rect.left - POPOUT_PADDING - POPOUT_BORDER,
      });
    }
  }, [defaultOpen, fixedPos, headerOffset]);

  // Close when the page (or any ancestor) scrolls, since the popout is
  // position: fixed to the row and would detach. Ignore scrolls originating
  // inside the popout's own scroll region — the capturing listener sees those
  // too, and closing on them makes the popout impossible to scroll.
  useEffect(() => {
    if (!open || defaultOpen) return;
    const handleScroll = (e: Event) => {
      if (popoutRef.current && e.target instanceof Node && popoutRef.current.contains(e.target)) return;
      if (hasLiveSelection()) return;
      close();
    };
    window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', handleScroll, { capture: true });
  }, [open, defaultOpen, close, hasLiveSelection]);

  // Selecting the popout's text to copy it. A drag that strays outside the
  // card, and the settled selection afterwards, both have to hold it open —
  // otherwise the text can only be read, never copied. Moving the pointer away
  // with nothing selected still dismisses it, and clicking anywhere else
  // dismisses it even with a selection held.
  useEffect(() => {
    if (!open || defaultOpen) return;

    const isInside = (target: EventTarget | null) =>
      target instanceof Node &&
      (popoutRef.current?.contains(target) || containerRef.current?.contains(target));

    // Pointer events, not mouse events: the scrollbar thumb cancels its
    // pointerdown, which suppresses the compatibility mousedown/mouseup.
    const handlePointerDown = (e: PointerEvent) => {
      if (popoutRef.current?.contains(e.target as Node)) {
        selectingRef.current = true;
        cancelClose();
        return;
      }
      if (!isInside(e.target)) close();
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (!selectingRef.current) return;
      selectingRef.current = false;
      if (!isInside(e.target) && !hasLiveSelection()) scheduleClose();
    };

    window.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('pointerup', handlePointerUp, true);
    window.addEventListener('pointercancel', handlePointerUp, true);
    return () => {
      window.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('pointerup', handlePointerUp, true);
      window.removeEventListener('pointercancel', handlePointerUp, true);
      selectingRef.current = false;
    };
  }, [open, defaultOpen, close, cancelClose, scheduleClose, hasLiveSelection]);

  // Align the first expanded row with the cell it overlays. Measured from the
  // real rendered layout, so the header's height (typography, counter, padding)
  // never needs to be hardcoded — headerOffset is only an initial estimate to
  // avoid a pre-correction flash. Runs before paint (useLayoutEffect).
  useLayoutEffect(() => {
    if (!open || defaultOpen || !contentRef.current || !fixedPos) return;
    const firstChild = contentRef.current.firstElementChild;
    if (!firstChild) return;
    const rect = firstChild.getBoundingClientRect();
    const topDiff = rect.top - targetPosRef.current.top;
    const leftDiff = rect.left - targetPosRef.current.left;
    if (Math.abs(topDiff) > 0.5 || Math.abs(leftDiff) > 0.5) {
      setFixedPos(prev => prev ? { top: prev.top - topDiff, left: prev.left - leftDiff } : null);
    }
  }, [open, defaultOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the card and its scrollbar visible when the source cell is near an edge.
  useLayoutEffect(() => {
    if (!open || !popoutRef.current || !fixedPos) return;
    const rect = popoutRef.current.getBoundingClientRect();
    const overflow = rect.bottom - window.innerHeight + POPOUT_PADDING;
    const left = Math.max(POPOUT_PADDING, Math.min(fixedPos.left, window.innerWidth - rect.width - POPOUT_PADDING));
    if (overflow > 0 || left !== fixedPos.left) {
      setFixedPos(prev => prev ? { top: prev.top - Math.max(0, overflow), left } : null);
    }
  }, [open, fixedPos]);

  const handleOpen = () => {
    cancelClose();
    // Opening unmounts the collapsed preview under the cursor, which makes the
    // browser re-fire mouseenter on this container. Ignore that re-entry: a
    // second anchor would reset fixedPos to the estimated position and discard
    // the measured alignment correction above.
    if (openRef.current || !containerRef.current) return;
    openRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    targetPosRef.current = { top: rect.top, left: rect.left };
    setFixedPos({
      top: rect.top - POPOUT_PADDING - POPOUT_BORDER - headerOffset,
      left: rect.left - POPOUT_PADDING - POPOUT_BORDER,
    });
    setOpen(true);
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      style={{ height: collapsedHeight }}
      data-state={open ? 'open' : undefined}
      onMouseEnter={handleOpen}
      onMouseLeave={scheduleClose}
    >
      {/* Collapsed preview with gradient fade. `isolate` keeps the fade's z-10
          inside this cell: a frozen table column is also sticky z-10, and the
          fade sits later in the DOM, so without it the fade paints over the
          frozen column and washes it out as the table scrolls. */}
      {!open && (
        <div className="relative isolate" style={{ height: collapsedHeight, overflow: 'hidden' }}>
          <div
            style={{ height: previewFadeHeight }}
            className="absolute inset-x-0 bottom-0 pointer-events-none z-10 bg-gradient-to-b from-transparent to-off-white group-hover:to-dinnerplate-white-lighter"
          />
          {collapsed}
        </div>
      )}

      {/* Expanded popout */}
      {/* `font-inter` is on the card, not left to callers: portalling to
          document.body escapes the table cell's font scope, and the app's
          default family is Funkis. Same rule as the sidebar's FloatingPopover
          shell — the portal owns its font so no call site can omit it.
          `pointer-events-auto` for the same reason: inside a modal sheet Radix
          sets `pointer-events: none` on body, which the card would inherit and
          close the moment the pointer left the cell for it. */}
      {open && fixedPos && createPortal(
        <div
          ref={popoutRef}
          className="pointer-events-auto fixed z-50 rounded-lg border border-dinnerplate-white bg-off-white font-inter"
          style={{
            top: fixedPos.top,
            left: fixedPos.left,
            width,
            maxWidth: `calc(100vw - ${2 * POPOUT_PADDING}px)`,
            padding: POPOUT_PADDING,
            boxShadow: '0 0 20px 3px rgba(239,239,239,0.5)',
            overflowX: 'hidden',
          }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          // The card is portalled out of the table, but React propagates
          // events through the component tree, not the DOM — so a click in
          // here still reaches the row's onClick and opens its drawer. That
          // makes the text impossible to select: releasing the drag navigates
          // away. Anything in the popout that should act on a click handles it
          // itself (see EventRow); nothing needs it to reach the row.
          onClick={(e) => e.stopPropagation()}
        >
          {header && (
            <div className="mb-2">
              {header}
              <hr className="mt-2 border-0 border-t border-dinnerplate-white" />
            </div>
          )}
          <div ref={contentRef}>
            {expanded}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
